import React, { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Video, Keyboard, BarChart3, LogOut, Menu } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import BrandLogo from "@/components/BrandLogo";
import AccountLockedScreen from "@/components/AccountLockedScreen";
import api from "@/lib/api";

export default function CandidateLayout() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [locked, setLocked] = useState(false);

  // Poll lock status every 20s so an admin unlock reflects promptly and any
  // in-page violation flips the whole shell into the lock takeover screen.
  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const r = await api.get("/candidate/status");
        if (alive) setLocked(!!r.data?.locked);
      } catch (e) {
        if (e?.response?.status === 423 && alive) setLocked(true);
      }
    };
    check();
    const iv = setInterval(check, 20000);
    const onLockedNow = () => setLocked(true);
    window.addEventListener("ds:candidate-locked", onLockedNow);
    return () => { alive = false; clearInterval(iv); window.removeEventListener("ds:candidate-locked", onLockedNow); };
  }, [loc.pathname]);

  const nav = [
    { to: "/dashboard", label: t("home"), icon: Home, end: true },
    { to: "/dashboard/training", label: t("training"), icon: Video },
    { to: "/dashboard/typing", label: t("typing"), icon: Keyboard },
    { to: "/dashboard/results", label: t("results"), icon: BarChart3 },
  ];

  const Sidebar = () => (
    <aside className="w-64 bg-[var(--ink)] text-white flex flex-col p-4 shrink-0 h-screen sticky top-0">
      <div className="px-2 mb-6">
        <BrandLogo size="md" dark />
        <div className="text-xs text-zinc-400 mt-1">Candidate Portal</div>
      </div>
      <div className="px-2 mb-4"><LanguageToggle dark /></div>
      <nav className="flex-1 flex flex-col gap-1">
        {nav.map((n) => {
          const Active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} data-testid={`cand-nav-${n.to.split("/").pop() || "home"}`}
              className={`sidebar-link ${Active ? "active" : ""}`} onClick={() => setOpen(false)}>
              <Icon size={18} /> {n.label}
            </Link>
          );
        })}
      </nav>
      <button data-testid="cand-logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
        className="sidebar-link mt-4 border-t border-zinc-800 pt-4">
        <LogOut size={18} /> {t("logout")}
      </button>
      <div className="text-xs text-zinc-500 mt-4 px-2">
        {t("login")}:<br /><span className="text-zinc-300">{user?.email}</span>
      </div>
    </aside>
  );

  return (
    <div className="flex bg-[var(--bg)] min-h-screen">
      {locked && <AccountLockedScreen />}
      <div className="hidden lg:block"><Sidebar /></div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative z-10"><Sidebar /></div>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-[var(--line)] flex items-center justify-between px-4 h-14">
          <button onClick={() => setOpen(true)}><Menu /></button>
          <BrandLogo size="sm" />
          <LanguageToggle />
        </header>
        <div className="p-6 lg:p-10 max-w-7xl">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
